import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BackButton } from '@/components/shared/BackButton';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchTemplate,
  createTemplate,
  updateTemplate,
} from '../../lib/inboxTemplatesApi';
import { QueryErrorState } from '@/components/shared/QueryErrorState';

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'fee_notes', label: 'Fee Notes' },
  { value: 'hearings', label: 'Hearings' },
  { value: 'conferences', label: 'Conferences' },
  { value: 'intake', label: 'Intake' },
  { value: 'correspondence', label: 'Correspondence' },
];


export default function InboxTemplateEditorPage() {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId?: string }>();
  const isEdit = !!templateId;
  const qc = useQueryClient();

  const { data: existing, isLoading: loadingExisting, isError: existingFailed, refetch } = useQuery({
    queryKey: ['inbox-template', templateId],
    queryFn: () => fetchTemplate(templateId!),
    enabled: isEdit,
  });

  const [name, setName] = useState('');
  const [category, setCategory] = useState('general');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setCategory(existing.category);
      setSubject(existing.subject);
      setBody(existing.body ?? '');
    }
  }, [existing]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isEdit && templateId) return updateTemplate(templateId, { name, category, subject, body });
      return createTemplate({ name, category, subject, body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox-templates'] });
      navigate('/barrister/inbox/settings/templates');
    },
    onError: () => setError('Failed to save template.'),
  });

  function handleSave() {
    if (!name.trim()) { setError('Name is required.'); return; }
    if (!subject.trim()) { setError('Subject is required.'); return; }
    if (!body.trim()) { setError('Body is required.'); return; }
    setError('');
    saveMutation.mutate();
  }

  const inputStyle: React.CSSProperties = {
    border: '1px solid var(--border-strong)', borderRadius: 6,
    padding: '10px 12px', fontSize: 16, color: 'var(--text-primary)',
    fontFamily: "'IBM Plex Sans', sans-serif",
    outline: 'none', width: '100%', boxSizing: 'border-box' as const,
  };

  return (
    <div style={{
      maxWidth: 820, margin: '0 auto',
      padding: 'clamp(20px, 5vw, 40px) clamp(14px, 4vw, 24px)',
      fontFamily: "'IBM Plex Sans', sans-serif",
    }}>
      {/* Was an unlabelled arrow wired to a fixed destination. These pages are opened from the
          inbox, from the Email settings hub and from Settings, so a fixed target sent people
          somewhere they had never been. Back means back — same control as every other page. */}
      <BackButton onClick={() => navigate(-1)} />

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 28 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
          {isEdit ? 'Edit template' : 'New template'}
        </h1>
      </div>

      {/* Editing, but the template could not be fetched: the form would otherwise sit blank
          under the heading "Edit template", and a save would write that blank over the stored
          template. An empty form is not an empty template. */}
      {isEdit && existingFailed ? (
        <QueryErrorState what="this template" onRetry={() => void refetch()} />
      ) : isEdit && loadingExisting ? (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>Loading…</div>
      ) : (
        <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', display: 'block', marginBottom: 6 }}>
                Name
              </label>
              <input
                style={inputStyle}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Template name"
              />
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', display: 'block', marginBottom: 6 }}>
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                style={{
                  ...inputStyle,
                  background: 'var(--surface-card)', cursor: 'pointer',
                }}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', display: 'block', marginBottom: 6 }}>
              Subject
            </label>
            <input
              style={inputStyle}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
            />
          </div>

          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', display: 'block', marginBottom: 6 }}>
              Body
            </label>
            <textarea
              style={{
                ...inputStyle,
                minHeight: 260, resize: 'vertical', lineHeight: 1.6,
              }}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Email body"
            />
          </div>
        </div>

        {/* Save controls close the page, below the fields rather than inside them. The 28px
            matches the rhythm the filter builder uses between its sections — without it the
            buttons sat flush against the body field, which reads as part of the field. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 28 }}>
          {error && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 6, padding: '8px 14px', fontSize: 13, color: '#dc2626',
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <button
              onClick={handleSave} className="btn-mobile-full"
              disabled={saveMutation.isPending}
              style={{
                padding: '11px 22px', borderRadius: 6, flex: 1, minWidth: 150,
                background: '#1e3a5f', color: 'white',
                border: 'none', cursor: 'pointer',
                fontSize: 14, fontWeight: 500,
                opacity: saveMutation.isPending ? 0.6 : 1,
              }}
            >
              {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create template'}
            </button>
            <button
              onClick={() => navigate('/barrister/inbox/settings/templates')} className="btn-mobile-full"
              style={{
                padding: '11px 22px', borderRadius: 6, flex: 1, minWidth: 150,
                background: 'var(--surface-card)', color: 'var(--text-primary)',
                border: '1px solid var(--border-default)', cursor: 'pointer',
                fontSize: 14,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
        </>
      )}
    </div>
  );
}
